import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { BookOpen, Droplets, Moon, Apple, Dumbbell, ArrowRight, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { usePageSEO } from "../hooks/usePageSEO";

const RecoveryGuidePage = () => {
  usePageSEO();
  const topics = [
    {
      icon: Droplets,
      title: "Hydration Basics",
      description: "Understanding proper hydration before, during, and after recovery sessions for general wellness.",
      points: ["Water intake guidelines", "Electrolyte basics", "Signs of dehydration"]
    },
    {
      icon: Moon,
      title: "Rest & Sleep",
      description: "The role of quality sleep in supporting your overall wellness routine.",
      points: ["Sleep duration recommendations", "Pre-sleep routines", "Recovery days importance"]
    },
    {
      icon: Apple,
      title: "Nutrition Fundamentals",
      description: "General nutrition concepts to support your training and wellness goals.",
      points: ["Macronutrient basics", "Meal timing concepts", "Whole food focus"]
    },
    {
      icon: Dumbbell,
      title: "Active Recovery",
      description: "Low-intensity activities that may support your rest days and overall routine.",
      points: ["Stretching basics", "Light movement options", "Foam rolling concepts"]
    }
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
              <BookOpen className="w-4 h-4 text-[#b89a6d]" />
              <span className="text-sm text-[#b89a6d] font-medium">Educational Content</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#3a2a26] mb-4">
              Wellness & Recovery Guide
            </h1>
            <p className="text-xl text-[#7a6b62] max-w-3xl mx-auto mb-6">
              Educational resources on training recovery, hydration, nutrition, and wellness concepts.
            </p>
            <div className="bg-white border border-[#f0d8d4] rounded-xl px-6 py-4 inline-block">
              <p className="text-sm text-[#7a6b62]">
                <Shield className="w-4 h-4 inline mr-2 text-[#b89a6d]" />
                <span className="text-[#5a4a40]">Educational content only.</span> Not medical advice.
              </p>
            </div>
          </div>

          {/* Topics Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {topics.map((topic) => (
              <div
                key={topic.title}
                className="bg-white border border-[#f0d8d4] rounded-2xl p-8 hover:border-[#d4a5ad] transition-all"
              >
                <div className="w-14 h-14 bg-[#fdf2f4] rounded-xl flex items-center justify-center mb-6">
                  <topic.icon className="w-7 h-7 text-[#b89a6d]" />
                </div>
                <h3 className="text-xl font-bold text-[#3a2a26] mb-3">{topic.title}</h3>
                <p className="text-[#7a6b62] mb-4">{topic.description}</p>
                <ul className="space-y-2">
                  {topic.points.map((point) => (
                    <li key={point} className="flex items-center gap-2 text-[#5a4a40] text-sm">
                      <div className="w-1.5 h-1.5 bg-[#b89a6d] rounded-full" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Key Principles */}
          <div className="bg-gradient-to-r from-[#d4a5ad]/10 to-[#b89a6d]/10 border border-[#f0d8d4] rounded-2xl p-8 mb-16">
            <h2 className="text-2xl font-bold text-[#3a2a26] mb-6 text-center">General Wellness Principles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#b89a6d] mb-2">Consistency</div>
                <p className="text-[#7a6b62] text-sm">Regular routines support overall wellness</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#b89a6d] mb-2">Balance</div>
                <p className="text-[#7a6b62] text-sm">Training, rest, and nutrition work together</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#b89a6d] mb-2">Listen</div>
                <p className="text-[#7a6b62] text-sm">Pay attention to how your body responds</p>
              </div>
            </div>
          </div>

          {/* CTA to Shop */}
          <div className="text-center bg-white border border-[#f0d8d4] rounded-2xl p-12">
            <h2 className="text-2xl font-bold text-[#3a2a26] mb-4">
              Explore Recovery Support Products
            </h2>
            <p className="text-[#7a6b62] mb-8 max-w-2xl mx-auto">
              Browse our selection of wellness products with transparent ingredient labeling and clear product information.
            </p>
            <Link to="/shop">
              <Button className="bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-bold px-10 py-6 text-lg">
                Visit Shop
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <p className="text-[#a09186] text-xs mt-6">
              Products for general wellness purposes. Not medical advice.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="mt-12 p-6 bg-white/80 border border-[#f0d8d4] rounded-xl">
            <p className="text-xs text-[#a09186] text-center">
              <strong className="text-[#7a6b62]">Disclaimer:</strong> This content is for general educational purposes only and does not constitute medical advice. 
              The information provided is not intended to diagnose, treat, cure, or prevent any disease. 
              Always consult with a qualified healthcare professional before starting any new aesthetic or wellness routine.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RecoveryGuidePage;
